import createUrlHash from '../../../functions/create-url-hash'
import EventBus from '../../../functions/event-bus'
import getScrollPosition from '../../../functions/get-scroll-position'
import getUrlHash from '../../../functions/get-url-hash'

import './link'

/**
 * Find accordion from URL hash
 * @returns {HTMLElement} Accordion element
 */
function findLinkedAccordion () {
  const accordionHash = getUrlHash().accordion

  if (accordionHash) {
    return document.querySelector(`#${accordionHash}.js-accordion`) || false
  }
}

class Accordion {
  /**
   * Create new accordion
   * @param {HTMLElement} container Accordion container
   */
  constructor (container) {
    this.container = container
    this.button = container.querySelector('.js-accordion-button')
    this.panel = container.querySelector('.js-accordion-panel')

    this.id = container.id
    this.isExpanded = false

    // Get parent which might be an alphabet group
    const parent = container.parentNode

    // Pre-open accordion if it's the first child of an alphabet group
    if (parent.classList.contains('js-alphabet-group') && parent.firstElementChild === container) {
      EventBus.subscribe('alphabet', alphabetId => {
        const linkedAccordion = findLinkedAccordion()

        // Only pre-open if no accordion is linked or a linked accordion
        // is not in the current alphabet group
        if ((!linkedAccordion || !parent.contains(linkedAccordion)) && parent.id === alphabetId) {
          this.open(false)
        }
      })
    }

    this.button.addEventListener('click', () => {
      this.toggle()
    })

    // Pre-open accordion if it's referenced by hash
    if (this.id === getUrlHash().accordion) {
      this.open()
    }

    // Close accordion when another one opens
    EventBus.subscribe('accordion', id => {
      if (id !== this.id) {
        this.close()
      }
    })
  }

  /**
   * Open accordion
   * @param {Boolean} changeUrlHash Change URL hash to link to current accordion
   */
  open (changeUrlHash = true) {
    this.button.classList.add('is-expanded')
    this.panel.classList.add('is-expanded')

    this.button.setAttribute('aria-expanded', 'true')
    this.panel.setAttribute('aria-expanded', 'true')

    this.isExpanded = true

    EventBus.publish('accordion', this.id)

    if (changeUrlHash) {
      const state = { accordion: this.id }
      history.replaceState(state, null, createUrlHash(state))

      this.scrollTo()

      // Wait for CSS to finalize on intial scroll
      document.addEventListener('DOMContentLoaded', () => {
        this.scrollTo()
      })
    } else {
      const state = { accordion: '' }
      history.replaceState(state, null, createUrlHash(state))
    }
  }

  /**
   * Close accordion
   */
  close () {
    this.button.classList.remove('is-expanded')
    this.panel.classList.remove('is-expanded')

    this.button.setAttribute('aria-expanded', 'false')
    this.panel.setAttribute('aria-expanded', 'false')

    this.isExpanded = false
  }

  /**
   * Toggle open and close state
   */
  toggle () {
    if (this.isExpanded) {
      this.close()

      const state = { accordion: '' }
      history.replaceState(state, null, createUrlHash(state))
    } else {
      this.open()
    }
  }

  scrollTo () {
    const scrollPosition =
      this.container.getBoundingClientRect().top +
      getScrollPosition()

    window.scrollTo(0, scrollPosition)
  }
}

const containers = document.querySelectorAll('.js-accordion')
const accordions = []

// Initialize new accordions
containers.forEach(container => accordions.push(new Accordion(container)))

// Open first accordion if there's no accordion already opened by URL hash
if (!findLinkedAccordion() && accordions.length) {
  accordions[0].open(false)
}

// Listen for hash changes (for example by a link)
// Open the corresponding accordion if the ID matches
window.addEventListener('hashchange', () => {
  accordions.forEach(accordion => {
    if (accordion.id === getUrlHash().accordion) {
      accordion.open()
    }
  })
})
